import { useMutation, useQuery } from "@tanstack/react-query";

/** Responses mirror services/pdf-lambda/src/terms/signatureHandlers.ts contracts. */
export interface TermSignatureView {
  status: "in_progress" | "completed" | "expired";
  term: { title: string; renderedHtml?: string };
  signatory?: { name: string; emailMasked: string; method: string };
  signedPdfAvailable: boolean;
}

export interface VerifyCpfResult {
  ok: boolean;
}

export interface SignResult {
  ok: boolean;
  alreadySigned?: boolean;
}

function lambdaBase(): string {
  const url = import.meta.env.VITE_PDF_LAMBDA_URL as string | undefined;
  if (!url) throw new Error("pdf_lambda_url_not_configured");
  return url.replace(/\/$/, "");
}

async function parseError(resp: Response): Promise<never> {
  let code = `http_${resp.status}`;
  try {
    const json = await resp.json();
    if (json?.error) code = json.error as string;
  } catch {
    /* ignore */
  }
  throw new Error(code);
}

/** Public URL that returns the signed PDF once completed. */
export function getSignedTermDownloadUrl(token: string): string {
  return `${lambdaBase()}/terms/sign/${encodeURIComponent(token)}/pdf`;
}

export function useTermSignatureView(token: string | undefined) {
  return useQuery({
    queryKey: ["term-signature-view", token],
    enabled: Boolean(token),
    retry: false,
    queryFn: async (): Promise<TermSignatureView> => {
      const resp = await fetch(`${lambdaBase()}/terms/sign/${encodeURIComponent(token!)}`);
      if (!resp.ok) return parseError(resp);
      return (await resp.json()) as TermSignatureView;
    },
  });
}

export function useVerifyResponsibleCpf(token: string) {
  return useMutation({
    mutationFn: async (cpf: string): Promise<VerifyCpfResult> => {
      const resp = await fetch(`${lambdaBase()}/terms/sign/${encodeURIComponent(token)}/verify-cpf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cpf }),
      });
      if (!resp.ok) return parseError(resp);
      return (await resp.json()) as VerifyCpfResult;
    },
  });
}

export interface SignTermInput {
  cpf: string;
  typedName: string;
  consent: boolean;
}

export function useSignTerm(token: string) {
  return useMutation({
    mutationFn: async (input: SignTermInput): Promise<SignResult> => {
      const resp = await fetch(`${lambdaBase()}/terms/sign/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cpf: input.cpf,
          typed_name: input.typedName,
          consent: input.consent,
        }),
      });
      if (!resp.ok) return parseError(resp);
      return (await resp.json()) as SignResult;
    },
  });
}
